/**
 * The scrolling transcript — task 5 (Tossker repaint), README §4 "Thread
 * pane": inbound `--surface-card` + 1px `--border-subtle` + `--shadow-xs`,
 * radius `16 16 16 4`; outbound **solid `--brand-primary`, white text**,
 * radius `16 16 4 16`, with a "Delivered" `check-check` line under it; both
 * 14px/1.65, padding 10px 13px, max-width 62%. System events are centred
 * `--surface-sunken` pills at 11.5px with a leading icon. Sender/time meta
 * at 10px above the first bubble of a run; a three-dot pulsing capsule for
 * client typing; an unsent message shows "not sent · transport down" plus
 * Retry.
 *
 * DELIVERED IS DERIVED, NOT FABRICATED. `Message` carries no separate
 * delivery-status field — `notSent` is the only send-state fact on the wire
 * (packages/shared/src/types.ts). An outbound message that renders here at
 * all and is not flagged `notSent` has gone through, so "Delivered" is shown
 * for exactly that case (`outbound && !m.notSent`) rather than inventing a
 * field this data model does not have.
 *
 * `clientTyping` has no real data source yet — no field for "the client is
 * typing" exists anywhere in ThreadSummary/ThreadDetail (Peer only models
 * staff presence). This prop lets the capsule be exercised and proves the
 * component supports it once a presence signal exists; `ThreadPane` always
 * passes `false` today. See task-15-report.md's seams section.
 *
 * IMAGE THUMBNAILS — task 8 (media images, 2026-09-04). `Message.media`
 * (absent means text-only, per its own doc comment in
 * packages/shared/src/types.ts) renders as one `<img>` per `MediaRef`,
 * fetched through `useMediaBlob` (a staff-session-gated blob URL, never a
 * bare `src="/ops/media/:id"` — that route has no session cookie a plain
 * `<img>` tag could carry in every browser, and 401ing a broken-image icon
 * silently is worse than never trying it unauthenticated at all). Split into
 * `MessageImage` below, ONE COMPONENT PER MEDIA ITEM, specifically so the
 * hook — which needs its own `useState`/`useEffect` per id — is never called
 * from inside `messages.map`'s callback body directly, which would be
 * calling a hook a variable number of times per render (rules-of-hooks
 * violation) instead of mounting a variable number of components that each
 * call it once.
 *
 * MOBILE BLOWOUT LAW (Andrew, two real ones this week): every thumbnail is
 * `max-h-[240px] max-w-full object-contain rounded-[8px]` — NEVER an
 * unconstrained intrinsic size. A photo straight off a phone camera is
 * routinely 3000-4000px on a side; rendered at native size in a 62%-wide
 * bubble it blows the whole layout off the side of a phone screen exactly
 * the way the sys-pill's unbroken recording URL did (see this file's own
 * sys-pill comment below) — a different mechanism, the identical failure
 * mode.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import type { MediaRef, Message } from '@communicator/shared'
import { Icon } from '../ui'
import { useMediaBlob } from '../../lib/useMediaBlob'
import { bubbleCornerClasses, formatMessageStamp, isRunStart } from './format'

/**
 * One thumbnail. `failed` renders nothing rather than a broken-image icon —
 * a vanished or inaccessible media row is not the customer's or the
 * dispatcher's problem to stare at, and the message body beside it still
 * carries the conversation.
 *
 * CLICK-TO-OPEN (whole-branch review, I1): the spec called for the
 * thumbnail to open the signed-in full-size view (`/media/:id`, MediaPage.tsx)
 * on click, and that got dropped along the way — the bubble rendered the
 * image with no way to see it larger. Wrapped in a plain `<a>` rather than
 * an onClick handler so it's a real link (new-tab, middle-click, "open in
 * new window" all work for free) — `target="_blank"` plus `rel="noreferrer"`
 * so the opened tab can't reach back into this one via `window.opener`.
 */
function MessageImage({ media }: { media: MediaRef }) {
  const { url, failed } = useMediaBlob(media.id)
  if (failed || !url) return null
  return (
    <a href={`/media/${media.id}`} target="_blank" rel="noreferrer" data-testid={`message-image-link-${media.id}`}>
      <img
        data-testid={`message-image-${media.id}`}
        src={url}
        alt="Attached image"
        className="max-h-[240px] max-w-full rounded-[8px] object-contain"
      />
    </a>
  )
}

/**
 * How close to the bottom (in px) still counts as "following along" rather
 * than "scrolled up to read history." Comfortably larger than sub-pixel
 * rounding slop (a few px), but tight enough that scrolling up even one
 * message bubble's worth reads as deliberate — see task-44-report.md.
 */
const BOTTOM_PIN_THRESHOLD_PX = 48

function isNearBottom(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_PIN_THRESHOLD_PX
}

export interface TranscriptProps {
  messages: Message[]
  onRetry?: (message: Message) => void
  clientTyping?: boolean
  /**
   * Suppresses the Retry affordance on an unsent message. The closed-thread
   * read-only view (`ReadOnlyTranscript`) has no operation buttons anywhere
   * on the page — close is terminal, so a Retry there would be a
   * contradiction even though no current fixture leaves an unsent message on
   * a closed thread. The "not sent" fact itself still renders; only the
   * button is gone.
   */
  readOnly?: boolean
  /**
   * The viewer's own `p.tz` preference (2026-08-24 design), or `'auto'`
   * (the pref's own default) when omitted — never raw UTC. Passed in by the
   * caller (`ThreadPane`, `ReadOnlyTranscript`) rather than read from the
   * store here, so this stays a component that renders exactly what it is
   * given, matching every other prop on this list.
   */
  tz?: string
}

export function Transcript({ messages, onRetry, clientTyping = false, readOnly = false, tz = 'auto' }: TranscriptProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  // Whether the user is anchored to the bottom right now. A ref, not state —
  // it's read on every message update but changing it should never itself
  // trigger a render (only handleScroll and the effect below care).
  const pinnedRef = useRef(true)
  const mountedRef = useRef(false)
  const threadKeyRef = useRef<string | null>(null)
  const signatureRef = useRef('')
  const [showJump, setShowJump] = useState(false)

  // Messages all share one threadId; using the first message's is a cheap,
  // reliable proxy for "which conversation is this" without a dedicated prop.
  const threadKey = messages[0]?.threadId ?? null
  const signature = messages.map((m) => m.id).join(',')

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const isNewThread = !mountedRef.current || threadKey !== threadKeyRef.current
    mountedRef.current = true
    threadKeyRef.current = threadKey

    if (isNewThread) {
      // Opening a conversation (or switching to a different one): land at the
      // newest message immediately — no smooth/animated scroll, which on
      // first paint would just look like a glitch.
      signatureRef.current = signature
      pinnedRef.current = true
      setShowJump(false)
      el.scrollTop = el.scrollHeight
      return
    }

    if (signature === signatureRef.current) {
      // Same thread, same messages — a re-render that didn't change the
      // list. Touching scrollTop here is exactly the bug being fixed.
      return
    }
    signatureRef.current = signature

    if (pinnedRef.current) {
      // User is at (or near) the bottom: follow the new message down.
      el.scrollTop = el.scrollHeight
    } else {
      // User deliberately scrolled up to read history — leave them alone,
      // just flag that there's something new below.
      setShowJump(true)
    }
  }, [messages, threadKey, signature])

  function handleScroll() {
    const el = scrollRef.current
    if (!el) return
    pinnedRef.current = isNearBottom(el)
    if (pinnedRef.current) setShowJump(false)
  }

  function jumpToLatest() {
    const el = scrollRef.current
    if (!el) return
    pinnedRef.current = true
    setShowJump(false)
    el.scrollTop = el.scrollHeight
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        data-testid="transcript"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3"
      >
        {messages.map((m, i) => {
          if (m.from === 'sys') {
            return (
              <div
                key={m.id}
                data-testid={`message-${m.id}`}
                // max-w-full + [overflow-wrap:anywhere], not break-words: this
                // pill was built for one-liners ("Ticket 64640 created."), then
                // call-journal notes poured multi-line transcripts with
                // ~400-char unbroken recording URLs into it, and `w-fit` sized
                // it to the URL — blowing the whole layout off the side of a
                // phone (Andrew, 2026-09-03). break-word does not constrain
                // min-content width in a flex column; `anywhere` does.
                // items-start + pre-wrap keep a multi-line body readable
                // beside its icon instead of centring against it.
                className="inline-flex w-fit max-w-full min-w-0 items-start gap-1.5 self-center whitespace-pre-wrap rounded-[20px] bg-sunken px-3 py-[5px] text-left text-[11.5px] text-mid [overflow-wrap:anywhere]"
              >
                <Icon name="info" size={11} className="mt-[3px] flex-none text-lo" />
                {m.body}
              </div>
            )
          }

          if (m.from === 'note') {
            // A NOTE IS NOT A BUBBLE, deliberately. It is what a technician
            // wrote to themselves during a call, and it never went to the
            // client. Rendering it in either bubble shape would make it
            // ambiguous, and a note mistaken for a sent message reads as a
            // promise nobody made.
            //
            // So: no bubble geometry, no left/right alignment, a pencil and
            // the author's name, on the accent tint pair — which is already
            // designed and contrast-checked in all four themes, rather than a
            // new hue invented here.
            return (
              <div key={m.id} data-testid={`message-${m.id}`} className="flex flex-col items-stretch">
                <div
                  className="flex items-start gap-2 rounded-control px-[13px] py-[10px] text-[13.5px] leading-[1.6]"
                  style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}
                >
                  <Icon name="pencil" size={13} className="mt-[3px] flex-none" />
                  <div className="min-w-0">
                    <div className="mb-[2px] text-[10px] font-semibold uppercase tracking-[.08em] opacity-80">
                      {(m.authorName ?? 'Staff (name unavailable)')} · note · {formatMessageStamp(m.at, tz)}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  </div>
                </div>
              </div>
            )
          }

          const outbound = m.from === 'us'
          const showMeta = isRunStart(messages, i)
          // This is a SHARED console — "You" read differently for each
          // viewer and made every outbound line indistinguishable from every
          // other tech's. `authorName` is the resolved staff display name
          // (see Message.authorName's doc comment for why it is never
          // `author` itself); "Staff (name unavailable)" matches the wording
          // `halo/outbox.ts` already uses for the same unresolvable-author
          // case, rather than inventing a second phrasing for the same fact.
          const authorLabel = outbound
            ? (m.authorName ?? 'Staff (name unavailable)')
            : (m.authorName ?? m.author ?? 'Them')

          return (
            <div key={m.id} className={`flex flex-col ${outbound ? 'items-end' : 'items-start'}`}>
              {showMeta && (
                <div className="mb-1 text-[10px] text-lo">
                  {authorLabel} · {formatMessageStamp(m.at, tz)}
                </div>
              )}
              {m.alsoInThreadId && (
                // THE CLIENT IS TEXTING IN TWO PLACES. We could not know which
                // conversation this belongs to — they were on a call with us
                // and mid-SMS about something else — so it was delivered to
                // both rather than guessed at. Said plainly on every copy,
                // because the confusing moment is not this message, it is the
                // one three replies later where the context stops making
                // sense.
                //
                // ON THE TINT, NOT BARE ON THE CARD. --on-tint-accent is
                // contrast-checked against --tint-accent and nothing else;
                // Legacy Day deliberately DARKENS it (--legacy-warn-dim) to
                // clear 4.5:1 there, which leaves it ~2:1 against the card.
                // Painted as bare text this warning was in the DOM and
                // invisible on screen — Andrew reported it simply missing.
                // The pair is guarded as `kind: 'text'` in tokens.test.ts, so
                // splitting it again fails that test rather than shipping.
                <div
                  data-testid={`message-${m.id}-also-in-thread`}
                  className="mb-1 flex items-start gap-1.5 rounded-control px-2 py-1 text-[10.5px] leading-[1.45]"
                  style={{ background: 'var(--tint-accent)', color: 'var(--on-tint-accent)' }}
                >
                  <Icon name="triangle-alert" size={11} className="mt-[2px] flex-none" />
                  <span>Also sent to another open conversation with this client — it may not relate to this one.</span>
                </div>
              )}
              <div
                data-testid={`message-${m.id}`}
                className={[
                  'max-w-[62%] px-[13px] py-[10px] text-[14px] leading-[1.65]',
                  bubbleCornerClasses(outbound),
                  // Fix round 2 (finding 4): was `bg-cc-blue text-white` —
                  // white text on the plain --brand-primary fill was
                  // 3.00:1 in Legacy, 3.49:1 in Tossker Night, 4.35:1 in
                  // Tossker Day, all under 4.5:1 for this 14px body text.
                  // `bg-brand-primary-strong` is a darker step tuned so
                  // `text-on-brand` (still white) clears 4.5:1 in every
                  // theme — `--brand-primary` itself, used for buttons and
                  // borders elsewhere, is untouched.
                  outbound ? 'bg-brand-primary-strong text-on-brand' : 'border border-subtle bg-card text-hi shadow-xs',
                ].join(' ')}
              >
                {m.body}
                {m.media && m.media.length > 0 && (
                  <div className="mt-1.5 flex flex-col gap-1.5">
                    {m.media.map((media) => (
                      <MessageImage key={media.id} media={media} />
                    ))}
                  </div>
                )}
                {outbound && !m.notSent && (
                  // Fix round 2: was `text-white/80` — 2.43:1 in Legacy.
                  // Full-opacity `text-on-brand` against the new darker
                  // fill clears 4.5:1; the dimming isn't needed for the
                  // "secondary" read once the icon + smaller size do that.
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-on-brand">
                    <Icon name="check-check" size={11} />
                    Delivered
                  </div>
                )}
                {m.notSent && (
                  <div className={['mt-1 flex items-center gap-2 text-[11px]', outbound ? 'text-on-brand' : 'text-bad'].join(' ')}>
                    <span>not sent · transport down</span>
                    {!readOnly && (
                      <button type="button" className="underline" onClick={() => onRetry?.(m)}>
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
        {clientTyping && (
          <div data-testid="typing-capsule" className="flex w-fit items-center gap-1 self-start rounded-[12px] border border-subtle bg-card px-3 py-2">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="h-[6px] w-[6px] animate-cc-pulse rounded-full"
                style={{ backgroundColor: 'var(--cc-text-mid)', animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        )}
      </div>
      {showJump && (
        <button
          type="button"
          data-testid="jump-to-latest"
          onClick={jumpToLatest}
          // Fix (visual findings, item 1): was `bg-cc-blue text-hi` — same
          // bug as Button.tsx's primary variant, same fix (`text-on-brand`
          // on `bg-brand-primary-strong`, the fill this file's own outbound
          // bubble already uses for exactly this reason).
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-primary-strong px-3 py-1.5 text-[11px] font-semibold text-on-brand"
        >
          New messages
        </button>
      )}
    </div>
  )
}
